import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useParams } from '@tanstack/react-router';
import { meetingsApi } from '../features/meetings/api.ts';
import { notesApi, pageIdOf, pageLabel, pagesQueryKey, GENERAL_PAGE } from '../features/notes/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { Icon } from '../components/Icon.tsx';

// The shell for a person's private notebook for one meeting: the meeting header
// and the page list, with the open page rendered into the Outlet.
//
// Each page is its own ROUTE (/notes/general, /notes/<agendaItemId>), which is
// what keeps the editor's autosave honest: only ever ONE live document, torn
// down and re-seeded by the page component when the route changes, rather than
// documents swapped in place underneath a running debounce.

export function MeetingNotesLayout(): JSX.Element {
  const { meetingId } = useParams({ from: '/meetings/$meetingId/notes' });
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? null;
  // The open page, read from the URL rather than from a child's params so the
  // shell needs no coupling to which child route is mounted.
  const activePageId = useLocation().pathname.split('/').filter(Boolean).pop() ?? GENERAL_PAGE;
  const [listOpen, setListOpen] = useState(false);

  const meeting = useQuery({ queryKey: ['meeting', meetingId], queryFn: () => meetingsApi.get(meetingId) });
  // hasNote and updatedAt are PERSONAL, so like the notes themselves this cache
  // entry is scoped to the viewer: ssoBootstrap can swap the authenticated user
  // in place with no reload, and on a shared council-office machine an unscoped
  // key would show user B which of user A's pages have writing on them.
  const pages = useQuery({
    queryKey: pagesQueryKey(userId, meetingId),
    queryFn: () => notesApi.fetchPages(meetingId),
    enabled: userId !== null,
  });

  const active = pages.data?.find((p) => pageIdOf(p) === activePageId);

  function list(): JSX.Element {
    if (!pages.data) return <p className="p-3 text-sm text-muted-foreground">{dv.common.loading}</p>;
    return (
      <ul className="space-y-1">
        {pages.data.map((page) => {
          const id = pageIdOf(page);
          const isActive = id === activePageId;
          return (
            <li key={id}>
              <Link
                to="/meetings/$meetingId/notes/$pageId"
                params={{ meetingId, pageId: id }}
                onClick={() => setListOpen(false)}
                aria-current={isActive ? 'page' : undefined}
                className={`flex items-start gap-2 rounded-2xl px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-[hsl(var(--primary)/0.12)] font-semibold text-[hsl(var(--primary))]'
                    : 'hover:bg-[hsl(var(--chrome))]'
                }`}
              >
                {/* A written page is marked with a quiet dot rather than a
                    filled chip: nothing here is pressable but the row itself. */}
                <span
                  aria-hidden="true"
                  className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
                    page.hasNote ? 'bg-[hsl(var(--primary))]' : 'bg-transparent'
                  }`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block break-words">{pageLabel(page)}</span>
                  {!page.onAgenda && (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {dv.notes.offAgenda}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }

  if (meeting.isError || pages.isError) {
    return (
      <div className="page space-y-3">
        <p className="text-sm text-muted-foreground" role="alert">{dv.common.error}</p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => { void meeting.refetch(); void pages.refetch(); }}
          >
            {dv.common.retry}
          </button>
          <Link to="/meetings/$meetingId" params={{ meetingId }} className="btn-secondary btn-sm">
            <Icon name="arrow_forward" size={16} /> {dv.common.back}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page space-y-6 lg:space-y-8">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="status-label">{dv.notes.title}</p>
          <h1 className="text-3xl font-bold tracking-tight">{meeting.data?.title ?? ''}</h1>
          <p className="text-sm text-muted-foreground">{dv.notes.privateHint}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link to="/meetings/$meetingId" params={{ meetingId }} className="btn-secondary btn-sm">
            <Icon name="arrow_forward" size={16} /> {dv.common.back}
          </Link>
        </div>
      </div>

      {/* Mobile: the page list is a disclosure headed by the open page's name,
          so the whole notebook is one tap away without stealing the screen. */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setListOpen((v) => !v)}
          aria-expanded={listOpen}
          aria-label={dv.notes.title}
          className="btn-secondary btn-sm w-full justify-between"
        >
          <span className="truncate">{active ? pageLabel(active) : dv.notes.title}</span>
          <Icon name={listOpen ? 'expand_less' : 'expand_more'} size={18} />
        </button>
        {listOpen && <nav className="mt-2 rounded-3xl bg-[hsl(var(--chrome))] p-2">{list()}</nav>}
      </div>

      <div className="grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start">
        <nav className="hidden lg:block lg:sticky lg:top-6 rounded-3xl bg-[hsl(var(--chrome))] p-2">
          {list()}
        </nav>
        <Outlet />
      </div>
    </div>
  );
}
